import { apiClient } from '@api/apiClient';
import { V4ApiResponse } from '@api/types';
import { ToastType } from '@components';
import { AddOrEditCommentPayload } from '@store/customers/customerDetails';
// import { Comments } from '@store/orders';
import { setCarrierDetailsChangeBaseline } from '@changeTracking/pages/carrierDetails/formChangeTracking';
import { buildChangeTrackingRequestConfig } from '@changeTracking/utils/buildChangeTrackingHeaders';
import type { ChangeTrackingSource } from '@typeDefinitions/changeTrackingTypes';
import { Comments } from '@typeDefinitions/comments';
import { createError } from '@utils/errorUtils';
import { handlePopupToast, handleToast } from '@utils/utils';

import { setCarrierDetailsStore } from './store';
import {
  AddCarrierContactPayload,
  CarrierAdjustmentAccounts,
  CarrierCSPAccount,
  CarrierDetailLoadHistoryResponse,
  CarrierDetails,
  CarrierDetailsAdjustment,
  CarrierDetailsClaimHistory,
  CarrierDocument,
  CarrierFuelSurchargeDetails,
  CarrierReactivationRequestCacheItemT,
  CarrierScac,
  Contact,
  CountrySupport,
  CSPEnabledCustomerLookup,
  FactoringCompany,
  HighwayRuleCategoryViewModel,
  LoadHistoryPayload,
} from './types';

export const fetchCarrierDetails = async (carrierId: string) => {
  try {
    const response: V4ApiResponse<CarrierDetails> = await apiClient.get(
      `/carrier/carrierdetails/${carrierId}`,
    );

    if (response.isSuccess) {
      return response.value;
    }
  } catch (e: unknown) {
    const error = createError(e, 'Fetching carrier details failed');
    handleToast(ToastType.Error, error.message);
  }
};

export const carrierReactivationFromProfile = async (
  carrierId: number,
  comment?: string,
  changeTrackingSource?: ChangeTrackingSource,
) => {
  try {
    const response: V4ApiResponse<CarrierReactivationRequestCacheItemT> =
      await apiClient.post(
        `/carrier/carrierreactivation/${carrierId}`,
        {
          comment: comment ?? '',
        },
        buildChangeTrackingRequestConfig(
          changeTrackingSource ? [changeTrackingSource] : [],
        ),
      );

    if (response.isSuccess) {
      return response.value;
    }
  } catch (e: unknown) {
    throw createError(e, 'Reactivation error');
  }
};

export const carrierReactivationFromLoad = async (
  carrierId: number,
  loadId: number,
  comment?: string,
  changeTrackingSource?: ChangeTrackingSource,
) => {
  try {
    const response: V4ApiResponse<CarrierReactivationRequestCacheItemT> =
      await apiClient.post(
        `/carrier/carrierreactivation/${carrierId}/${loadId}`,
        { comment: comment ?? '' },
        buildChangeTrackingRequestConfig(
          changeTrackingSource ? [changeTrackingSource] : [],
        ),
      );

    if (response.isSuccess) {
      return response.value;
    }
  } catch (e: unknown) {
    throw createError(e, 'Reactivation error');
  }
};

export const fetchCarrierAdjustments = async (carrierId: string) => {
  try {
    const response: V4ApiResponse<CarrierDetailsAdjustment[]> =
      await apiClient.get(`/Carrier/Adjustments/${carrierId}`, {
        params: { carrierId },
      });
    return response;
  } catch (e: unknown) {
    const error = createError(e, 'Fetching carrier adjustments failed');
    handleToast(ToastType.Error, error.message);
  }
};

export const fetchCarrierAdjustmentAccounts = async () => {
  try {
    const response: V4ApiResponse<CarrierAdjustmentAccounts[]> =
      await apiClient.get('/carrier/adjustmentaccounts');
    if (response.isSuccess) {
      return response.value;
    }
  } catch (e: unknown) {
    const error = createError(
      e,
      'Fetching carrier adjustments accounts failed',
    );
    handleToast(ToastType.Error, error.message);
  }
};
export const fetchCarrierDocuments = async (carrierId: string) => {
  try {
    const response: V4ApiResponse<CarrierDocument[]> = await apiClient.get(
      `/Carrier/getcarrierdocument/${carrierId}`,
    );
    return response.value;
  } catch (e: unknown) {
    const error = createError(e, 'Fetching carrier documents failed');
    handleToast(ToastType.Error, error.message);
  }
};

export const fetchCarrierFuelSurcharge = async (carrierId: string) => {
  try {
    const resp: V4ApiResponse<CarrierFuelSurchargeDetails> =
      await apiClient.get(
        `/FuelSurcharge/getfuelsurcharge?ownerId=${carrierId}&ownerType=${1}&includeDeleted=${false}`,
      );
    if (resp.isSuccess) {
      return resp.value;
    }
  } catch (e: unknown) {
    const error = createError(e, 'Fetching carrier fuel surcharge failed');
    handleToast(ToastType.Error, error.message);
  }
};

export const addCarrierToPlaylistFavorite = async (
  payload: {
    selected: boolean;
    carrierId: number;
  },
  changeTrackingSource?: ChangeTrackingSource,
) => {
  try {
    const resp: V4ApiResponse<number> = await apiClient.put(
      '/Playlist/Favorite',
      payload,
      buildChangeTrackingRequestConfig(
        changeTrackingSource ? [changeTrackingSource] : [],
      ),
    );
    return resp;
  } catch (e: unknown) {
    const error = createError(e, 'Adding carrier playlist favorite failed');
    handleToast(ToastType.Error, error.message);
  }
};

export const fetchCarrierFactoringCompany = async (carrierId: number) => {
  try {
    const response: V4ApiResponse<FactoringCompany> = await apiClient.get(
      `/FactoringCompany/getfactoringdata/${carrierId}`,
    );
    return response.value;
  } catch (e: unknown) {
    const error = createError(e, 'Fetching carrier factoring company failed');
    handleToast(ToastType.Error, error.message);
  }
};

export const postCarrierComment = async (
  payload: AddOrEditCommentPayload,
  changeTrackingSource?: ChangeTrackingSource,
) => {
  try {
    const res: V4ApiResponse<Comments> = await apiClient.post(
      '/Carrier/addcarriercomment',
      payload,
      buildChangeTrackingRequestConfig(
        changeTrackingSource ? [changeTrackingSource] : [],
      ),
    );
    return res;
  } catch (e: unknown) {
    const error = createError(e, 'Adding carrier comments failed');
    handleToast(ToastType.Error, error.message);
  }
};

export async function deleteCarrierComment(
  commentId: number,
  changeTrackingSource?: ChangeTrackingSource,
) {
  try {
    const res: V4ApiResponse<null> = await apiClient.delete(
      `/Carrier/deletecomment/${commentId}`,
      buildChangeTrackingRequestConfig(
        changeTrackingSource ? [changeTrackingSource] : [],
      ),
    );
    return res.isSuccess;
  } catch (e: unknown) {
    const error = createError(e, 'Deleting carrier comments failed');
    handleToast(ToastType.Error, error.message);
  }
}

export const fetchCarrierLoadHistory = async (payload: LoadHistoryPayload) => {
  try {
    const response: V4ApiResponse<CarrierDetailLoadHistoryResponse> =
      await apiClient.post('Carrier/LoadHistory', payload);
    if ('value' in response) {
      return response.value;
    }
  } catch (e: unknown) {
    const error = createError(e, 'Fetching carrier load history failed');
    handleToast(ToastType.Error, error.message);
  }
};

export const fetchCarrierRuleCodingMappingAndData = async () => {
  const response: V4ApiResponse<HighwayRuleCategoryViewModel[]> =
    await apiClient.get('Carrier/HighwayRuleCodingMappings');
  if ('value' in response) {
    return response.value;
  }
};

export const saveNewCarrierAdjustments = async (
  carrierId: number,
  payload: Record<string, unknown>[],
  changeTrackingSource?: ChangeTrackingSource,
) => {
  try {
    const res: V4ApiResponse<null> = await apiClient.post(
      '/Carrier/Adjustment',
      {
        id: carrierId,
        adjustments: payload,
      },
      buildChangeTrackingRequestConfig(
        changeTrackingSource ? [changeTrackingSource] : [],
      ),
    );
    return res.isSuccess;
  } catch (e: unknown) {
    const error = createError(e, 'Saving new carrier adjustments failed');
    handleToast(ToastType.Error, error.message);
  }
};

export const fetchPastCarrierAdjustments = async (carrierId: number) => {
  try {
    const response: V4ApiResponse<CarrierDetailsAdjustment[]> =
      await apiClient.get(
        `/Carrier/AdjustmentHistory/${carrierId}?toCurrency=NaN`,
      );
    if (response.isSuccess) {
      return response.value;
    }
  } catch (e: unknown) {
    const error = createError(e, 'Fetching past carrier adjustments failed');
    handleToast(ToastType.Error, error.message);
  }
};

export const updateCarrierComment = async (
  payload: AddOrEditCommentPayload,
  changeTrackingSource?: ChangeTrackingSource,
) => {
  try {
    const res: { isSuccess: boolean } = await apiClient.put(
      '/Carrier/updatecomment',
      payload,
      buildChangeTrackingRequestConfig(
        changeTrackingSource ? [changeTrackingSource] : [],
      ),
    );
    return res.isSuccess;
  } catch (e: unknown) {
    const error = createError(e, 'Updating carrier comments failed');
    handleToast(ToastType.Error, error.message);
  }
};

export const updateCarrierDetailsRatings = async (
  payload: Record<string, string | number | null>,
  changeTrackingSource?: ChangeTrackingSource,
) => {
  try {
    const res: V4ApiResponse<null> = await apiClient.put(
      '/Carrier/updaterating',
      payload,
      buildChangeTrackingRequestConfig(
        changeTrackingSource ? [changeTrackingSource] : [],
      ),
    );
    return res.isSuccess;
  } catch (e: unknown) {
    const error = createError(e, 'Updating carrier ratings failed');
    handleToast(ToastType.Error, error.message);
  }
};

export const sendCarrierTransfloEmails = async (
  payload: {
    carrierId: number;
    contactIds: number[];
  },
  changeTrackingSource?: ChangeTrackingSource,
) => {
  try {
    const resp: V4ApiResponse<null> = await apiClient.post(
      '/carrier/enabletransflocarrier',
      payload,
      buildChangeTrackingRequestConfig(
        changeTrackingSource ? [changeTrackingSource] : [],
      ),
    );
    if (resp.isSuccess) {
      handleToast(
        ToastType.Success,
        'Sent Transflo email registration to selected contacts',
      );
    }
  } catch (e: unknown) {
    const error = createError(e, 'Sending carrier transflo emails failed');
    handleToast(ToastType.Error, error.message);
  }
};

export async function updateCarrierDetails(
  payload: CarrierDetails,
  changedSources: readonly ChangeTrackingSource[] = [],
) {
  try {
    const res: V4ApiResponse<CarrierDetails> = await apiClient.put(
      '/Carrier',
      payload,
      buildChangeTrackingRequestConfig(changedSources),
    );
    if (Boolean(res.isSuccess)) {
      handlePopupToast(ToastType.Success, 'Carrier details saved successfully');
      setCarrierDetailsStore('carrierDetails', res.value);
      setCarrierDetailsChangeBaseline(res.value);
      setCarrierDetailsStore(
        'showDuplicateBanner',
        Boolean(res.value.isDuplicate),
      );
      return res.isSuccess as boolean;
    }
  } catch (e: unknown) {
    const error = createError(e, 'Updating carrier details failed');
    handleToast(ToastType.Error, error.message);
  }
}

export const uploadCarrierDetailsDocument = async (
  carrierId: number,
  file: File,
  category: string,
  changeTrackingSource?: ChangeTrackingSource,
) => {
  try {
    const formData = new FormData();
    formData.append('Files', file);
    formData.append('DocumentEntityType', '3');
    formData.append('EntityId', carrierId.toString());
    formData.append('Category', category);
    const trackingConfig = buildChangeTrackingRequestConfig(
      changeTrackingSource ? [changeTrackingSource] : [],
    );
    const res: V4ApiResponse<{
      results: {
        documentId: number;
        name: string;
        url: string;
        taggedCategoryKeys: string[];
      }[];
    }> = await apiClient.post('/carrier/uploadfile', formData, {
      ...trackingConfig,
      headers: {
        ...trackingConfig?.headers,
        'Content-Type': 'multipart/form-data',
      },
    });
    return res.value;
  } catch (e: unknown) {
    const error = createError(e, 'Uploading carrier document failed');
    throw error;
  }
};

export async function deleteCarrierDetailsDocument(
  payload: {
    file: string;
    type: string;
  },
  changeTrackingSource?: ChangeTrackingSource,
) {
  try {
    await apiClient.delete('/carrier/deletefile', {
      ...buildChangeTrackingRequestConfig(
        changeTrackingSource ? [changeTrackingSource] : [],
      ),
      data: payload,
    });
  } catch (e: unknown) {
    const error = createError(e, 'Deleting carrier document failed');
    handleToast(ToastType.Error, error.message);
  }
}

export const uploadCarrierLogo = async (
  carrierId: number,
  file: File,
  changeTrackingSource?: ChangeTrackingSource,
) => {
  try {
    const formData = new FormData();
    formData.append('File', file);
    formData.append('CarrierId', carrierId.toString());
    const trackingConfig = buildChangeTrackingRequestConfig(
      changeTrackingSource ? [changeTrackingSource] : [],
    );
    const res: V4ApiResponse<{
      results: {
        name: string;
      }[];
    }> = await apiClient.post('/carrier/uploadLogo', formData, {
      ...trackingConfig,
      headers: {
        ...trackingConfig?.headers,
        'Content-Type': 'multipart/form-data',
      },
    });
    return res.value;
  } catch (e: unknown) {
    const error = createError(e, 'Uploading carrier logo failed');
    throw error;
  }
};

export const fetchCarrierDetailsClaimHistory = async (carrierId: string) => {
  try {
    const response: V4ApiResponse<CarrierDetailsClaimHistory[]> =
      await apiClient.get(`carrier/claimhistory/${carrierId}`);
    if (response.isSuccess) {
      return response.value;
    }
  } catch (e: unknown) {
    const error = createError(e, 'Fetching carrier claim history failed');
    handleToast(ToastType.Error, error.message);
  }
};

export const deleteCarrier = async (
  carrierId: number,
  changeTrackingSource?: ChangeTrackingSource,
) => {
  try {
    const res: V4ApiResponse<boolean> = await apiClient.delete(
      `/Carrier/${carrierId}`,
      buildChangeTrackingRequestConfig(
        changeTrackingSource ? [changeTrackingSource] : [],
      ),
    );
    return res.isSuccess;
  } catch (e: unknown) {
    const error = createError(e, 'Deleting carrier failed');
    handleToast(ToastType.Error, error.message);
  }
};

export const blacklistCarrier = async (params: {
  id: number;
  comment: string;
  changeTrackingSource?: ChangeTrackingSource;
}) => {
  try {
    const res: V4ApiResponse<null> = await apiClient.post(
      `/Carrier/addtoblacklist?id=${params.id}&comment=${params.comment}`,
      undefined,
      buildChangeTrackingRequestConfig(
        params.changeTrackingSource ? [params.changeTrackingSource] : [],
      ),
    );
    return res.isSuccess;
  } catch (e: unknown) {
    const error = createError(e, 'Blacklisting carrier failed');
    handleToast(ToastType.Error, error.message);
  }
};

export const unblacklistCarrier = async (
  carrierId: number,
  changeTrackingSource?: ChangeTrackingSource,
) => {
  try {
    const res: V4ApiResponse<null> = await apiClient.put(
      `/Carrier/removefromblacklist?id=${carrierId}`,
      undefined,
      buildChangeTrackingRequestConfig(
        changeTrackingSource ? [changeTrackingSource] : [],
      ),
    );
    return res.isSuccess;
  } catch (e: unknown) {
    const error = createError(e, 'Unblacklisting carrier failed');
    handleToast(ToastType.Error, error.message);
  }
};

export async function saveCarrierDetails(
  payload: CarrierDetails,
  changedSources: readonly ChangeTrackingSource[] = [],
) {
  try {
    const res: V4ApiResponse<CarrierDetails> = await apiClient.post(
      '/Carrier',
      payload,
      buildChangeTrackingRequestConfig(changedSources),
    );

    // The declared response type promises a CarrierDetails value, but a
    // misbehaving backend can still return a null/undefined value.
    const carrier = res.value as CarrierDetails | null | undefined;
    if (!carrier) {
      throw new Error('No carrier was returned by the server.');
    }

    handlePopupToast(ToastType.Success, 'Carrier details saved successfully');
    return carrier.id;
  } catch (e: unknown) {
    const error = createError(e, 'Saving carrier details failed');
    handleToast(ToastType.Error, error.message);
  }
}

export const fetchCarrierScac = async (carrierId: number) => {
  try {
    const response: V4ApiResponse<CarrierScac[]> = await apiClient.get(
      `/ltl/carrierscac/Carrier/${carrierId}`,
    );
    if (response.isSuccess) {
      return response.value;
    }
  } catch (e: unknown) {
    const error = createError(e, 'Fetching carrier scac failed');
    handleToast(ToastType.Error, error.message);
  }
};

export const addCarrierScac = async (
  carrierId: number,
  scac: string,
  trackingUrl: string,
  countrySupport: CountrySupport,
  changeTrackingSource?: ChangeTrackingSource,
) => {
  try {
    const response: V4ApiResponse<number> = await apiClient.post(
      '/ltl/carrierscac',
      {
        carrierId,
        scac,
        trackingUrl,
        ...countrySupport,
      },
      buildChangeTrackingRequestConfig(
        changeTrackingSource ? [changeTrackingSource] : [],
      ),
    );
    if (response.isSuccess) {
      return response.value;
    }
  } catch (e: unknown) {
    const error = createError(e, 'Adding carrier SCAC failed');
    handleToast(ToastType.Error, error.message);
  }
};

export const editCarrierScac = async (
  carrierId: number,
  scac: string,
  trackingUrl: string,
  carrierScacId: number,
  countrySupport: CountrySupport,
  changeTrackingSource?: ChangeTrackingSource,
) => {
  try {
    const response: V4ApiResponse<number> = await apiClient.put(
      `/ltl/carrierscac/carrier/${carrierScacId}`,
      {
        carrierId,
        scac,
        trackingUrl,
        ...countrySupport,
      },
      buildChangeTrackingRequestConfig(
        changeTrackingSource ? [changeTrackingSource] : [],
      ),
    );
    if (response.isSuccess) {
      return response.value;
    }
  } catch (e: unknown) {
    const error = createError(e, 'Editing carrier scac failed');
    handleToast(ToastType.Error, error.message);
  }
};

export const fetchCarrierCSPCustomers = async (carrierId: number) => {
  try {
    const response: V4ApiResponse<CarrierCSPAccount[]> = await apiClient.get(
      `/ltl/csp/Carrier/${carrierId}`,
    );
    if (response.isSuccess) {
      return response.value;
    }
  } catch (e: unknown) {
    const error = createError(e, 'Fetching carrier CSP customers failed');
    handleToast(ToastType.Error, error.message);
  }
};

export const fetchCSPEnabledCustomers = async (scacId: number) => {
  try {
    const response: V4ApiResponse<CSPEnabledCustomerLookup[]> =
      await apiClient.get(
        `/ltl/csp/enabledcustomers?excludeCarrierScacId=${scacId}`,
      );
    if (response.isSuccess) {
      return response.value;
    }
  } catch (e: unknown) {
    const error = createError(e, 'Failed to fetch CSP enabled customers');
    handleToast(ToastType.Error, error.message);
  }
};

export const addCarrierContact = async (
  payload: AddCarrierContactPayload,
  changeTrackingSource?: ChangeTrackingSource,
): Promise<Contact | undefined> => {
  try {
    const response: V4ApiResponse<Contact> = await apiClient.post(
      '/Carrier/addcarriercontact',
      payload,
      changeTrackingSource
        ? buildChangeTrackingRequestConfig([changeTrackingSource])
        : undefined,
    );
    if (response.isSuccess) {
      return response.value;
    }
  } catch (e: unknown) {
    const error = createError(e, 'Adding carrier contact failed');
    handleToast(ToastType.Error, error.message);
    throw error;
  }
};
